import React, { useEffect, useState } from 'react';
import { FaTimes, FaSave } from 'react-icons/fa';
import api, { createLedgerEntry } from '../../services/api';

const LedgerModal = ({ isOpen, onClose, defaultAccountId = '', defaultDescription = '' }) => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ account: '', date: '', description: '', debit: 0, credit: 0 });

  useEffect(() => {
    if (!isOpen) return;
    const loadAccounts = async () => {
      try {
        const res = await api.get('/api/accounts', { params: { limit: 1000 } });
        setAccounts(res.data?.accounts || []);
      } catch (e) {
        setAccounts([]);
      }
    };
    loadAccounts();
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setForm({ account: defaultAccountId || '', date: '', description: defaultDescription || '', debit: 0, credit: 0 });
      setError('');
    }
  }, [isOpen, defaultAccountId, defaultDescription]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const payload = {
        account: form.account,
        date: form.date || undefined,
        description: form.description,
        debit: Number(form.debit || 0),
        credit: Number(form.credit || 0)
      };
      if (!payload.account) throw new Error('Please select an account');
      if ((payload.debit || 0) > 0 && (payload.credit || 0) > 0) {
        throw new Error('Enter either debit or credit, not both');
      }
      if ((payload.debit || 0) === 0 && (payload.credit || 0) === 0) {
        throw new Error('Enter a debit or credit amount');
      }
      await createLedgerEntry(payload);
      onClose(true);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to create ledger entry');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={() => onClose(false)}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Add Ledger Entry</h2>
          <button className="modal-close" onClick={() => onClose(false)}>
            <FaTimes />
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} className="billing-form">
          <div className="form-section">
            <h3>Entry Details</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="ledger-account">Account *</label>
                <select id="ledger-account" name="account" value={form.account} onChange={handleChange} required>
                  <option value="">Select an account</option>
                  {accounts.map(acc => (
                    <option key={acc._id} value={acc._id}>{acc.accountCode} - {acc.accountName}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="ledger-date">Date</label>
                <input type="date" id="ledger-date" name="date" value={form.date} onChange={handleChange} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="ledger-debit">Debit</label>
                <input type="number" id="ledger-debit" name="debit" value={form.debit} onChange={handleChange} min="0" step="0.01" />
              </div>
              <div className="form-group">
                <label htmlFor="ledger-credit">Credit</label>
                <input type="number" id="ledger-credit" name="credit" value={form.credit} onChange={handleChange} min="0" step="0.01" />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="ledger-description">Description</label>
              <textarea id="ledger-description" name="description" value={form.description} onChange={handleChange} rows="3" placeholder="Enter description" />
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={() => onClose(false)} disabled={loading}>
              <FaTimes /> Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              <FaSave /> {loading ? 'Saving...' : 'Add Entry'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LedgerModal;


